import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Modal,
  Button,
  Badge,
  Combobox,
  DateInput,
  NumberInput,
  Input,
  DetailModal,
} from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { AUDIT_COLUMNS, sortByLabel } from '../../../config/columns';
import {
  remittanceService,
  bankAccountService,
  currencyService,
  customerService,
  beneficiaryService,
  partnerService,
  nationalityService,
} from '../../../services/resources';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';
const isoDate = (v) => (v ? String(v).slice(0, 10) : '');

const STATUS_TONE = { PENDING: 'blue', SENT: 'green', CANCELLED: 'gray' };

const emptyForm = () => ({
  transaction_date: today(),
  customer_id: '',
  beneficiary_id: '',
  bnf_name: '',
  bnf_nationality_id: '',
  bnf_bank_name: '',
  bnf_swift_code: '',
  bnf_account_no: '',
  currency_id: '',
  amount: '',
  received_bank_id: '',
  service_charge: '',
  service_charge_bank_id: '',
  remark: '',
  idempotency_key: '',
});

const emptyProcess = () => ({
  process_date: today(),
  partner_id: '',
  partner_reference_no: '',
  transfer_fee: '',
  cost_bank_id: '',
  principal_paid_bank_id: '',
});

export default function RemittancePage({ onToast }) {
  const { can } = useAuth();
  const canView = can('REMITTANCE', 'CanView');
  const canCreate = can('REMITTANCE', 'CanCreate');
  const canEdit = can('REMITTANCE', 'CanEdit');
  const canDelete = can('REMITTANCE', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const [banks, setBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [defaultCharge, setDefaultCharge] = useState(100000);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [processTarget, setProcessTarget] = useState(null);
  const [pform, setPform] = useState(emptyProcess());
  const [perrors, setPerrors] = useState({});

  const [viewRow, setViewRow] = useState(null); // read-only view mode

  // action: { type: 'revert' | 'cancel' | 'delete', row }
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      setRows(await remittanceService.list(from, to));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const changeDateFrom = (v) => {
    setDateFrom(v);
    load(v, dateTo);
  };
  const changeDateTo = (v) => {
    setDateTo(v);
    load(dateFrom, v);
  };

  useEffect(() => {
    load();
    Promise.all([
      bankAccountService.list(),
      currencyService.list(),
      customerService.list(),
      beneficiaryService.list(),
      partnerService.list(),
      nationalityService.list(),
      remittanceService.defaults(),
    ])
      .then(([b, c, cu, bn, pt, n, d]) => {
        setBanks(b);
        setCurrencies(c);
        setCustomers(cu);
        setBeneficiaries(bn);
        setPartners(pt);
        setNationalities(n);
        if (d && d.service_charge !== undefined) setDefaultCharge(Number(d.service_charge));
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const currencyCode = (id) => currencies.find((c) => String(c.id) === String(id))?.currency_code;
  const idrId = currencies.find((c) => c.currency_code === 'IDR')?.id;

  const bankOpts = (pred) =>
    sortByLabel(
      banks
        .filter((b) => !b.is_blocked && pred(b))
        .map((b) => ({ value: b.id, label: `${b.bank_name} (${currencyCode(b.currency_id) || ''})` }))
    );

  const idrBankOptions = bankOpts((b) => idrId !== undefined && b.currency_id === idrId);
  const bankOptionsFor = (currencyId) =>
    currencyId ? bankOpts((b) => String(b.currency_id) === String(currencyId)) : [];

  const customerOptions = sortByLabel(
    customers.filter((c) => !c.is_blocked).map((c) => ({ value: c.id, label: c.customer_name }))
  );
  const beneficiaryOptions = sortByLabel(
    beneficiaries.filter((b) => !b.is_blocked).map((b) => ({ value: b.id, label: b.beneficiary_name }))
  );
  const partnerOptions = sortByLabel(
    partners.filter((p) => !p.is_blocked).map((p) => ({ value: p.id, label: p.partner_name }))
  );
  const nationalityOptions = sortByLabel(
    nationalities.map((n) => ({ value: n.id, label: n.nationality_name }))
  );
  const currencyOptions = sortByLabel(
    currencies.map((c) => ({ value: c.id, label: c.currency_code }))
  );

  const FIELD_VALID = {
    transaction_date: (v) => !!v,
    customer_id: (v) => !!v,
    beneficiary_id: (v) => !!v,
    bnf_name: (v) => !!String(v).trim(),
    bnf_nationality_id: (v) => !!v,
    currency_id: (v) => !!v,
    amount: (v) => Number(v) > 0,
    received_bank_id: (v) => !!v,
  };

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const check = FIELD_VALID[k];
      if (check && check(v)) {
        const next = { ...prev };
        delete next[k];
        return next;
      }
      return prev;
    });
  };

  // Picking a beneficiary populates the snapshot fields from the master (still editable).
  const pickBeneficiary = (id) => {
    const b = beneficiaries.find((x) => String(x.id) === String(id));
    setForm((prev) => ({
      ...prev,
      beneficiary_id: id,
      bnf_name: b ? b.beneficiary_name || '' : '',
      bnf_nationality_id: b ? b.nationality_id || '' : '',
      bnf_bank_name: b ? b.bank_name || '' : '',
      bnf_swift_code: b ? b.swift_code || '' : '',
      bnf_account_no: b ? b.account_no || '' : '',
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.beneficiary_id;
      delete next.bnf_name;
      delete next.bnf_nationality_id;
      return next;
    });
  };

  // Changing the currency invalidates a previously chosen principal bank.
  const pickCurrency = (id) => {
    setForm((prev) => ({ ...prev, currency_id: id, received_bank_id: '' }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.currency_id;
      return next;
    });
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const keys = ['doc_no', 'customer_name', 'bnf_name', 'bnf_account_no', 'partner_name', 'partner_reference_no', 'status', 'remark'];
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search]);

  const openCreate = async () => {
    // Fetch the default fresh so a Settings change (or a slow initial load) is
    // never missed; fall back to the last known value if the call fails.
    let charge = defaultCharge;
    try {
      const d = await remittanceService.defaults();
      if (d && d.service_charge !== undefined) {
        charge = Number(d.service_charge);
        setDefaultCharge(charge);
      }
    } catch {
      /* keep the last known default */
    }
    setEditing(null);
    setForm({ ...emptyForm(), service_charge: String(charge), idempotency_key: crypto.randomUUID() });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      transaction_date: isoDate(row.transaction_date),
      customer_id: row.customer_id,
      beneficiary_id: row.beneficiary_id,
      bnf_name: row.bnf_name || '',
      bnf_nationality_id: row.bnf_nationality_id || '',
      bnf_bank_name: row.bnf_bank_name || '',
      bnf_swift_code: row.bnf_swift_code || '',
      bnf_account_no: row.bnf_account_no || '',
      currency_id: row.currency_id,
      amount: row.amount,
      received_bank_id: row.received_bank_id,
      service_charge: row.service_charge,
      service_charge_bank_id: row.service_charge_bank_id || '',
      remark: row.remark || '',
      idempotency_key: row.idempotency_key,
    });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.transaction_date) e.transaction_date = 'Date is required';
    if (!form.customer_id) e.customer_id = 'Customer is required';
    if (!form.beneficiary_id) e.beneficiary_id = 'Beneficiary is required';
    if (!String(form.bnf_name).trim()) e.bnf_name = 'Name is required';
    if (!form.bnf_nationality_id) e.bnf_nationality_id = 'Country is required';
    if (!form.currency_id) e.currency_id = 'Currency is required';
    if (!(Number(form.amount) > 0)) e.amount = 'Must be greater than 0';
    if (!form.received_bank_id) e.received_bank_id = 'Received bank is required';
    if (form.service_charge === '' || !(Number(form.service_charge) >= 0)) {
      e.service_charge = 'Enter 0 or more';
    } else if (Number(form.service_charge) > 0 && !form.service_charge_bank_id) {
      e.service_charge_bank_id = 'Bank is required when there is a service charge';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (savingRef.current) return;
    if (!validate()) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    const payload = {
      transaction_date: form.transaction_date,
      customer_id: Number(form.customer_id),
      beneficiary_id: Number(form.beneficiary_id),
      bnf_name: form.bnf_name,
      bnf_nationality_id: Number(form.bnf_nationality_id),
      bnf_bank_name: form.bnf_bank_name || null,
      bnf_swift_code: form.bnf_swift_code || null,
      bnf_account_no: form.bnf_account_no || null,
      currency_id: Number(form.currency_id),
      amount: Number(form.amount),
      received_bank_id: Number(form.received_bank_id),
      service_charge: Number(form.service_charge),
      service_charge_bank_id:
        Number(form.service_charge) > 0 && form.service_charge_bank_id
          ? Number(form.service_charge_bank_id)
          : null,
      remark: form.remark || null,
      idempotency_key: form.idempotency_key,
    };
    try {
      if (editing) {
        await remittanceService.update(editing.id, payload);
        onToast?.('Remittance updated');
      } else {
        await remittanceService.create(payload);
        onToast?.('Remittance saved');
      }
      setOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // --- Process -----------------------------------------------------------
  const openProcess = (row) => {
    const t = today();
    const txn = isoDate(row.transaction_date);
    setProcessTarget(row);
    setPform({ ...emptyProcess(), process_date: t < txn ? txn : t });
    setPerrors({});
    setSubmitError('');
  };

  const setP = (k, v) => {
    setPform((prev) => ({ ...prev, [k]: v }));
    setPerrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const submitProcess = async () => {
    if (savingRef.current) return;
    const e = {};
    if (!pform.process_date) e.process_date = 'Process date is required';
    else if (pform.process_date < isoDate(processTarget.transaction_date)) {
      e.process_date = 'Cannot be before the transaction date';
    }
    if (!pform.partner_id) e.partner_id = 'Partner / Agent is required';
    if (pform.transfer_fee === '' || !(Number(pform.transfer_fee) >= 0)) {
      e.transfer_fee = 'Enter 0 or more';
    } else if (Number(pform.transfer_fee) > 0 && !pform.cost_bank_id) {
      e.cost_bank_id = 'Bank is required when there is a transfer fee';
    }
    if (!pform.principal_paid_bank_id) e.principal_paid_bank_id = 'Principal paid from is required';
    setPerrors(e);
    if (Object.keys(e).length) return;

    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    try {
      await remittanceService.process(processTarget.id, {
        process_date: pform.process_date,
        partner_id: Number(pform.partner_id),
        partner_reference_no: pform.partner_reference_no || null,
        transfer_fee: Number(pform.transfer_fee),
        cost_bank_id: Number(pform.transfer_fee) > 0 ? Number(pform.cost_bank_id) : null,
        principal_paid_bank_id: Number(pform.principal_paid_bank_id),
      });
      onToast?.('Remittance processed');
      setProcessTarget(null);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // --- Revert / Cancel / Delete -----------------------------------------
  const openAction = (type, row) => {
    setAction({ type, row });
    setReason('');
  };

  const confirmAction = async () => {
    const { type, row } = action;
    try {
      if (type === 'revert') {
        await remittanceService.revert(row.id, reason);
        onToast?.('Reverted to Pending');
      } else if (type === 'cancel') {
        await remittanceService.cancel(row.id, reason);
        onToast?.('Remittance cancelled');
      } else {
        await remittanceService.remove(row.id);
        onToast?.('Remittance deleted');
      }
      setAction(null);
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  if (!canView) {
    return (
      <Card title="Remittance">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const columns = [
    {
      key: 'doc_no',
      label: 'Doc No',
      render: (r) => (
        <button
          type="button"
          onClick={() => setViewRow(r)}
          className="font-medium text-brand-dark hover:underline"
          title="View details"
        >
          {r.doc_no}
        </button>
      ),
    },
    { key: 'transaction_date', label: 'Date', render: (r) => isoDate(r.transaction_date) },
    { key: 'customer_name', label: 'Customer' },
    { key: 'bnf_name', label: 'Beneficiary' },
    { key: 'bnf_country', label: 'Country' },
    { key: 'currency_code', label: 'Currency' },
    { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), className: 'text-right' },
    { key: 'service_charge', label: 'Service Charge (IDR)', render: (r) => fmt(r.service_charge), className: 'text-right' },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'gray'}>{r.status}</Badge>,
    },
    { key: 'process_date', label: 'Process Date', render: (r) => isoDate(r.process_date) },
    { key: 'partner_name', label: 'Partner / Agent' },
    { key: 'partner_reference_no', label: 'Partner Ref' },
    { key: 'transfer_fee', label: 'Transfer Fee (IDR)', render: (r) => fmt(r.transfer_fee), className: 'text-right' },
    { key: 'remark', label: 'Remark' },
    ...AUDIT_COLUMNS,
    {
      key: '__a',
      label: 'Actions',
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          {canEdit && r.status === 'PENDING' && (
            <>
              <Button size="sm" onClick={() => openProcess(r)}>
                Process
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
                Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openAction('cancel', r)}>
                Cancel
              </Button>
            </>
          )}
          {canEdit && r.status === 'SENT' && (
            <Button size="sm" variant="secondary" onClick={() => openAction('revert', r)}>
              Revert to Pending
            </Button>
          )}
          {canDelete && r.status !== 'SENT' && (
            <Button size="sm" variant="danger" onClick={() => openAction('delete', r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const actionTitle = {
    revert: 'Revert to Pending',
    cancel: 'Cancel Remittance',
    delete: 'Delete Remittance',
  };

  return (
    <Card title="Remittance" actions={canCreate ? <Button onClick={openCreate}>+ New</Button> : null}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={dateFrom} onChange={changeDateFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={dateTo} onChange={changeDateTo} />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        loading={loading}
        maxHeight="60vh"
        className="rounded-md border border-gray-100"
      />
      <p className="mt-2 text-xs text-gray-400">{filtered.length} remittance(s)</p>

      {/* Record: create / edit */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${editing ? 'Edit' : 'New'} Remittance${editing ? ` — ${editing.doc_no}` : ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <DateInput
              label="Transaction Date"
              required
              value={form.transaction_date}
              error={errors.transaction_date}
              onChange={(v) => set('transaction_date', v)}
            />
            <Combobox
              label="Customer"
              required
              placeholder="-- select customer --"
              options={customerOptions}
              value={form.customer_id}
              error={errors.customer_id}
              onChange={(v) => set('customer_id', v)}
            />
          </div>

          <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Beneficiary
            </p>
            <div className="space-y-3">
              <Combobox
                label="Beneficiary"
                required
                placeholder="-- select beneficiary --"
                options={beneficiaryOptions}
                value={form.beneficiary_id}
                error={errors.beneficiary_id}
                onChange={pickBeneficiary}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Name"
                  required
                  value={form.bnf_name}
                  error={errors.bnf_name}
                  onChange={(e) => set('bnf_name', e.target.value.toUpperCase())}
                />
                <Combobox
                  label="Country"
                  required
                  placeholder="-- select country --"
                  options={nationalityOptions}
                  value={form.bnf_nationality_id}
                  error={errors.bnf_nationality_id}
                  onChange={(v) => set('bnf_nationality_id', v)}
                />
                <Input
                  label="Bank"
                  value={form.bnf_bank_name}
                  onChange={(e) => set('bnf_bank_name', e.target.value)}
                />
                <Input
                  label="Swift Code"
                  value={form.bnf_swift_code}
                  onChange={(e) => set('bnf_swift_code', e.target.value)}
                />
              </div>
              <Input
                label="Account No / IBAN"
                value={form.bnf_account_no}
                onChange={(e) => set('bnf_account_no', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Combobox
              label="Currency"
              required
              placeholder="-- select currency --"
              options={currencyOptions}
              value={form.currency_id}
              error={errors.currency_id}
              onChange={pickCurrency}
            />
            <NumberInput
              label="Amount"
              required
              value={form.amount}
              error={errors.amount}
              onChange={(v) => set('amount', v)}
            />
          </div>
          <Combobox
            label="Received Bank (principal)"
            required
            placeholder={form.currency_id ? '-- select bank --' : 'Select currency first'}
            options={bankOptionsFor(form.currency_id)}
            value={form.received_bank_id}
            error={errors.received_bank_id}
            onChange={(v) => set('received_bank_id', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <NumberInput
              label="Service Charge (IDR)"
              required
              value={form.service_charge}
              error={errors.service_charge}
              onChange={(v) => set('service_charge', v)}
            />
            <Combobox
              label="Service Charge Bank (IDR)"
              required={Number(form.service_charge) > 0}
              placeholder="-- select IDR bank --"
              options={idrBankOptions}
              value={form.service_charge_bank_id}
              error={errors.service_charge_bank_id}
              onChange={(v) => set('service_charge_bank_id', v)}
            />
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Remark (purpose / source of funds)
            </span>
            <textarea
              value={form.remark}
              onChange={(e) => set('remark', e.target.value)}
              style={{ resize: 'none' }}
              className="h-16 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
        </form>
      </Modal>

      {/* Process modal */}
      <Modal
        open={!!processTarget}
        onClose={() => setProcessTarget(null)}
        title={`Process Remittance${processTarget ? ` — ${processTarget.doc_no}` : ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setProcessTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submitProcess} disabled={saving}>
              {saving ? 'Processing…' : 'Mark as Sent'}
            </Button>
          </>
        }
      >
        {processTarget && (
          <div className="space-y-3">
            {submitError && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
            )}
            <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              {processTarget.customer_name} → {processTarget.bnf_name} ·{' '}
              <b>
                {processTarget.currency_code} {fmt(processTarget.amount)}
              </b>
            </p>
            <DateInput
              label="Process Date"
              required
              value={pform.process_date}
              error={perrors.process_date}
              onChange={(v) => setP('process_date', v)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Combobox
                label="Partner / Agent"
                required
                placeholder="-- select partner / agent --"
                options={partnerOptions}
                value={pform.partner_id}
                error={perrors.partner_id}
                onChange={(v) => setP('partner_id', v)}
              />
              <Input
                label="Partner Reference No"
                value={pform.partner_reference_no}
                onChange={(e) => setP('partner_reference_no', e.target.value)}
              />
            </div>
            <Combobox
              label={`Principal Paid From (${processTarget.currency_code})`}
              required
              placeholder="-- select bank --"
              options={bankOptionsFor(processTarget.currency_id)}
              value={pform.principal_paid_bank_id}
              error={perrors.principal_paid_bank_id}
              onChange={(v) => setP('principal_paid_bank_id', v)}
            />
            <div className="grid grid-cols-2 gap-3">
              <NumberInput
                label="Transfer Fee / Cost (IDR)"
                required
                value={pform.transfer_fee}
                error={perrors.transfer_fee}
                onChange={(v) => setP('transfer_fee', v)}
              />
              <Combobox
                label="Fee Paid From (IDR)"
                required={Number(pform.transfer_fee) > 0}
                placeholder="-- select IDR bank --"
                options={idrBankOptions}
                value={pform.cost_bank_id}
                error={perrors.cost_bank_id}
                onChange={(v) => setP('cost_bank_id', v)}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* View mode (read-only) */}
      <DetailModal
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        title={viewRow ? `Remittance — ${viewRow.doc_no}` : ''}
        sections={
          viewRow
            ? [
                {
                  title: 'Transaction',
                  items: [
                    { label: 'Doc No', value: viewRow.doc_no },
                    {
                      label: 'Status',
                      value: <Badge tone={STATUS_TONE[viewRow.status] || 'gray'}>{viewRow.status}</Badge>,
                    },
                    { label: 'Transaction Date', value: isoDate(viewRow.transaction_date) },
                    { label: 'Customer', value: viewRow.customer_name },
                    { label: 'Remark', value: viewRow.remark, full: true },
                  ],
                },
                {
                  title: 'Beneficiary',
                  items: [
                    { label: 'Name', value: viewRow.bnf_name },
                    { label: 'Country', value: viewRow.bnf_country },
                    { label: 'Bank', value: viewRow.bnf_bank_name },
                    { label: 'Swift Code', value: viewRow.bnf_swift_code },
                    { label: 'Account No / IBAN', value: viewRow.bnf_account_no },
                  ],
                },
                {
                  title: 'Amount',
                  items: [
                    {
                      label: 'Amount',
                      value: `${viewRow.currency_code || ''} ${fmt(viewRow.amount)}`.trim(),
                    },
                    { label: 'Received Bank (principal)', value: viewRow.received_bank_name },
                    { label: 'Service Charge (IDR)', value: fmt(viewRow.service_charge) },
                    { label: 'Service Charge Bank', value: viewRow.service_charge_bank_name },
                  ],
                },
                {
                  title: 'Processing',
                  items: [
                    { label: 'Process Date', value: isoDate(viewRow.process_date) },
                    { label: 'Partner / Agent', value: viewRow.partner_name },
                    { label: 'Partner Reference No', value: viewRow.partner_reference_no },
                    { label: 'Principal Paid From', value: viewRow.principal_paid_bank_name },
                    { label: 'Transfer Fee (IDR)', value: fmt(viewRow.transfer_fee) },
                    { label: 'Fee Paid From', value: viewRow.cost_bank_name },                  ],
                },
                {
                  title: 'Audit',
                  items: [
                    { label: 'Updated By', value: viewRow.updated_by },
                    {
                      label: 'Updated On',
                      value: viewRow.updated_on ? new Date(viewRow.updated_on).toLocaleString() : '',
                    },
                  ],
                },
              ]
            : []
        }
      />

      {/* Revert / Cancel / Delete confirmation */}
      <Modal
        open={!!action}
        onClose={() => setAction(null)}
        title={action ? `${actionTitle[action.type]} — ${action.row.doc_no}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAction(null)}>
              Back
            </Button>
            <Button variant={action?.type === 'revert' ? 'primary' : 'danger'} onClick={confirmAction}>
              Confirm
            </Button>
          </>
        }
      >
        {action && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {action.type === 'revert' &&
                'This clears the process details (date, partner, fee, banks) and sets the remittance back to Pending.'}
              {action.type === 'cancel' &&
                'This cancels the remittance. A cancelled remittance cannot be revived.'}
              {action.type === 'delete' && 'This will be permanently deleted. Continue?'}
            </p>
            {action.type !== 'delete' && (
              <Input
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}
